import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Reveal, Eyebrow, fadeUp } from "../components/Motion";

const SECTORS = ["all", "clean_tech", "biogas", "recycling", "natural_resources"];

export default function Portfolio() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/portfolio")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const visible =
    filter === "all" ? items : items.filter((i) => i.sector === filter);

  return (
    <div className="pt-32" data-testid="portfolio-page">
      <section className="px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <Eyebrow>{t.portfolio.eyebrow}</Eyebrow>
          <motion.h1
            {...fadeUp}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tighter leading-[1.02] mt-6 max-w-5xl"
          >
            {t.portfolio.title}
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="text-lg sm:text-xl text-[#4A4D4A] mt-8 max-w-2xl leading-relaxed"
          >
            {t.portfolio.lead}
          </motion.p>

          <div className="mt-12 flex flex-wrap gap-2" data-testid="portfolio-filters">
            {SECTORS.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-4 py-2 text-sm rounded-sm border transition-all ${
                  filter === s
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-white text-[#1A1A1A] border-[#1A1A1A]/15 hover:border-[#1A1A1A]"
                }`}
              >
                {s === "all" ? "All" : t.sectorLabels[s]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-16 sm:mt-20 px-6 md:px-10 pb-10">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <div className="text-sm text-[#7A7D7A]">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="text-sm text-[#7A7D7A]">{t.portfolio.empty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {visible.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.06}>
                  <article
                    className="group bg-white border border-[#EBEAE0] rounded-sm overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-500 h-full flex flex-col"
                    data-testid={`portfolio-card-${p.id}`}
                  >
                    <div className="aspect-[5/3] overflow-hidden bg-white border-b border-[#EBEAE0] flex items-center justify-center p-8">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="max-w-full max-h-full object-contain transition-transform duration-[1200ms] group-hover:scale-105"
                        />
                      ) : (
                        <div className="font-display text-3xl text-[#1A1A1A]/30 tracking-tight text-center px-4">
                          {p.name}
                        </div>
                      )}
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <div className="flex items-center justify-between">
                        <span className="text-xs tracking-[0.18em] uppercase text-[#A6192E]">
                          {t.sectorLabels[p.sector] || p.sector}
                        </span>
                        {p.status === "exited" && (
                          <span className="text-[10px] tracking-[0.18em] uppercase text-[#1A4331] border border-[#1A4331]/30 px-2 py-0.5 rounded-sm">
                            Exited
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-2xl text-[#1A1A1A] mt-3 tracking-tight">
                        {p.name}
                      </h3>
                      <p className="text-sm text-[#4A4D4A] mt-3 leading-relaxed flex-1">
                        {(lang === "de" && p.description_de) || p.description_en}
                      </p>
                      <div className="flex items-center justify-between mt-5 text-xs text-[#7A7D7A] font-mono">
                        <div className="flex items-center gap-3">
                          {p.country && <span>{p.country}</span>}
                          {p.year_invested && (
                            <>
                              <span className="text-[#1A1A1A]/20">·</span>
                              <span>since {p.year_invested}</span>
                            </>
                          )}
                        </div>
                        {p.website && (
                          <a
                            href={p.website}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-[#A6192E] link-underline normal-case"
                            data-testid={`portfolio-website-${p.id}`}
                          >
                            Visit ↗
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
